import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    View
} from "react-native";
import type {
    GenericInfoItemConfig,
    GenericInfoViewProps,
    GenericInfoKeyType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    MarkdownEditor,
    TextInput,
    EnUsIcon,
    TRTRIcon,
    Button,
    Text
} from "ncore-ui-kit";
import {
    ImageIcon,
    SaveIcon
} from "lucide-react-native";
import DriveSelectorModal from "../../../../components/driveSelectorModal";
import RESTServiceContext from "../../../../services/restAPI/context";

const GENERIC_INFO_KEYS: GenericInfoItemConfig[] = [
    {
        titleKey: "generic-info-key-privacy-policy",
        key: "privacy-policy"
    },
    {
        titleKey: "generic-info-key-member-policy",
        key: "member-policy"
    },
    {
        titleKey: "generic-info-key-about-konya",
        key: "about-konya"
    },
    {
        titleKey: "generic-info-key-about-us",
        key: "about-us"
    },
    {
        titleKey: "generic-info-key-kvkk",
        key: "kvkk-info"
    }
];

const GenericInfoView = ({
    refreshKey
}: GenericInfoViewProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        selectedKey,
        setSelectedKey
    ] = useState<GenericInfoKeyType>("about-us");

    const [
        selectedLanguage,
        setSelectedLanguage
    ] = useState<"tr-TR" | "en-US">("tr-TR");

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDriveModalVisible,
        setIsDriveModalVisible
    ] = useState<boolean>(false);

    const [
        title,
        setTitle
    ] = useState<string>("");

    const [
        content,
        setContent
    ] = useState<string>("");
    const [
        editorKey,
        setEditorKey
    ] = useState<number>(0);

    const normalizeMarkdownImages = (text: string) => {
        if(!text) {
            return "";
        }
        return text.replace(/!\[(.*?)\]\s*\((.*?)\)/g, "\n[image]($2)<100%x300>--contain--\n");
    };

    const handleDriveFileSelect = (url: string) => {
        setIsDriveModalVisible(false);
        const currentContent = content || "";
        const imageMarkdown = `\n[image](${url})<100%x300>--contain--\n`;
        setContent(currentContent + imageMarkdown);
        setEditorKey(prev => prev + 1);
    };

    const fetchGenericInfo = useCallback(async (key: GenericInfoKeyType, lang: "tr-TR" | "en-US") => {
        setIsLoading(true);
        try {
            const response = await RESTServiceContext.action("getGenericInfoByKey", {
                language: lang,
                key
            });

            if(response?.payload) {
                const data = response.payload;
                setTitle(data.title || "");
                setContent(normalizeMarkdownImages(data.content || ""));
            } else {
                setTitle("");
                setContent("");
            }
            setEditorKey(prev => prev + 1);
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("generic-info-fetch-error")
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        localize
    ]);

    useEffect(() => {
        fetchGenericInfo(selectedKey, selectedLanguage);
    }, [
        fetchGenericInfo,
        selectedKey,
        selectedLanguage,
        refreshKey
    ]);

    const handleKeySelect = (key: GenericInfoKeyType) => {
        setSelectedKey(key);
    };

    const handleLanguageSelect = (lang: "tr-TR" | "en-US") => {
        setSelectedLanguage(lang);
    };

    const handleSave = async () => {
        if(!title.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("generic-info-title-required")
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await RESTServiceContext.action("updateGenericInfo", {
                content: normalizeMarkdownImages(content),
                language: selectedLanguage,
                key: selectedKey,
                title
            });

            if(response?.isError) {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("generic-info-save-error")
                });
                return;
            }

            NCoreUIKitSnackBar.open({
                title: localize("generic-info-save-success")
            });
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("generic-info-save-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderKeySelectors = () => {
        return <View
            style={[
                stylesheet.keySelectorContainer,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            {GENERIC_INFO_KEYS.map((item) => {
                const isSelected = selectedKey === item.key;
                return <Button
                    customTextColor={isSelected ? colors.project.brandWhite : undefined}
                    variant={isSelected ? "filled" : "ghost"}
                    type={isSelected ? "primary" : "neutral"}
                    onPress={() => handleKeySelect(item.key)}
                    title={localize(item.titleKey as never)}
                    spreadBehaviour="free"
                    key={item.key}
                />;
            })}
        </View>;
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View>
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {localize("generic-info-title")}
                </Text>
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {localize("generic-info-description")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.languageSelector,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Button
                    customTextColor={selectedLanguage === "tr-TR" ? colors.project.brandWhite : undefined}
                    variant={selectedLanguage === "tr-TR" ? "filled" : "ghost"}
                    type={selectedLanguage === "tr-TR" ? "primary" : "neutral"}
                    onPress={() => handleLanguageSelect("tr-TR")}
                    spreadBehaviour="free"
                    icon={({
                        size
                    }) => <TRTRIcon
                        size={size}
                    />}
                    title="TR"
                />
                <Button
                    customTextColor={selectedLanguage === "en-US" ? colors.project.brandWhite : undefined}
                    variant={selectedLanguage === "en-US" ? "filled" : "ghost"}
                    type={selectedLanguage === "en-US" ? "primary" : "neutral"}
                    onPress={() => handleLanguageSelect("en-US")}
                    spreadBehaviour="free"
                    icon={({
                        size
                    }) => <EnUsIcon
                        size={size}
                    />}
                    title="EN"
                />
            </View>
        </View>;
    };

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        {renderHeader()}
        {renderKeySelectors()}
        <View
            style={[
                stylesheet.contentWrapper,
                {
                    backgroundColor: colors.content.container.default,
                    borderColor: colors.content.border.subtle,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            {
                isLoading ?
                    <View
                        style={stylesheet.loadingContainer}
                    >
                        <ActivityIndicator
                            color={colors.project.gold}
                            size="large"
                        />
                    </View>
                :
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                backgroundColor: colors.project.brandWhite,
                                borderColor: colors.project.brandBorder,
                                borderWidth: borders.seperator,
                                borderRadius: radiuses.md,
                                padding: spaces.spacingLg,
                                gap: spaces.spacingLg
                            }
                        ]}
                    >
                        <TextInput
                            placeholder={localize("generic-info-title-placeholder")}
                            title={localize("generic-info-title-label")}
                            spreadBehaviour="stretch"
                            onChangeText={setTitle}
                            value={title}
                        />
                        <View
                            style={[
                                stylesheet.editorSection,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                variant="titleSmallSize"
                                color="high"
                            >
                                {localize("generic-info-content-label")}
                            </Text>
                            <MarkdownEditor
                                key={`generic-editor-${selectedKey}-${selectedLanguage}-${editorKey}`}
                                spreadBehaviour="stretch"
                                onChangeText={setContent}
                                initialValue={content}
                                defaultMode="live"
                            />
                            <Button
                                title={localize("generic-info-add-image-drive")}
                                onPress={() => setIsDriveModalVisible(true)}
                                customTextColor={colors.project.brandWhite}
                                icon={({
                                    size
                                }) => <ImageIcon
                                    color={colors.project.brandWhite}
                                    size={size}
                                />}
                                customBorderColor="secondaryButton"
                                customColor="secondaryButton"
                                spreadBehaviour="free"
                                variant="filled"
                            />
                        </View>
                        <View
                            style={stylesheet.actionContainer}
                        >
                            <Button
                                customTextColor={colors.project.brandWhite}
                                icon={({
                                    size
                                }) => <SaveIcon
                                    color={colors.project.brandWhite}
                                    size={size}
                                />}
                                title={localize("save")}
                                isLoading={isSubmitting}
                                spreadBehaviour="free"
                                onPress={handleSave}
                                variant="filled"
                                type="primary"
                            />
                        </View>
                    </View>
            }
        </View>
        <DriveSelectorModal
            onClose={() => setIsDriveModalVisible(false)}
            onSelect={handleDriveFileSelect}
            isVisible={isDriveModalVisible}
        />
    </View>;
};
export default GenericInfoView;
